'use client';

import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

import { LOCAL_STORAGE_KEY } from 'constants/localStorageKey';
import PublicService from 'infraestruture/services/public';
import { Campaign } from 'infraestruture/services/public/dtos/response/CampaignListResponse';

export type FetchCampaignParams = {
  page?: number;
  search?: string;
  categoryId?: number | null;
};

export type FilterTypes =
  | 'campaigns'
  | 'subscriptions'
  | 'donations'
  | 'influencers';

export function useHomeController(searchParams: { [x: string]: string }) {
  const router = useRouter();
  const service = new PublicService();

  /** Refs de layout e input */
  const listMenuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /** Estados de layout */
  const [listMenuPosition, setListMenuPosition] = useState(0);
  const [listMenuWidth, setListMenuWidth] = useState(0);
  const [windowWidth, setWindowWidth] = useState(0);

  /** Estados de filtros e inputs */
  const [inputValue, setInputValue] = useState('');
  const [currentCategory, setCurrentCategory] = useState<number | null>(null);
  const [currentFilter, setCurrentFilter] = useState<FilterTypes>('campaigns');

  /** Estados de requisição */
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  /** Outros estados (categorias, toast, sort e modal) */
  const [categories, setCategories] = useState<{ id: number; title: string }[]>(
    []
  );
  const [showTermsToast, setShowTermsToast] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const sortOptions = [
    { value: 'popular', label: 'Popular' },
    { value: 'recent', label: 'Recente' },
  ];
  const [isOpen, setIsOpen] = useState(false);

  /** Controle de cancelamento de requisições */
  const abortControllerRef = useRef<AbortController | null>(null);

  /** Timeout para debounce de busca */
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  /** Função para buscar campanhas (suporta paginação) */
  const fetchCampaigns = async ({
    page = 1,
    search,
    categoryId,
  }: FetchCampaignParams) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    try {
      const response = await service.getCampaigns({
        page,
        search: search?.trim() || undefined,
        categoryId: categoryId ?? undefined,
        // signal: abortController.signal,
      });

      if (response?.statusCode === 200 && response.body?.data) {
        const newCampaigns = response.body.data;
        setCampaigns((prev) =>
          page === 1 ? newCampaigns : [...prev, ...newCampaigns]
        );
        setHasMore(response.body.meta.next_page_url !== null);
        setCurrentPage(response.body.meta.current_page);
      } else {
        setCampaigns([]);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Erro ao buscar campanhas:', error);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  /** Função para buscar categorias */
  const fetchCategories = async () => {
    try {
      const response = await service.getCategories();
      if (response?.statusCode === 200 && response.body) {
        setCategories(response.body);
      }
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
    }
  };

  /** Efeito inicial para obter parâmetros da URL, categorias e campanhas */
  useEffect(() => {
    const s = searchParams?.search ?? '';
    const c = searchParams?.category;

    setInputValue(s);
    setCurrentCategory(c ? Number(c) : null);

    fetchCategories();
    fetchCampaigns({
      page: 1,
      search: s,
      categoryId: c ? Number(c) : null,
    });
  }, []);

  /** Efeito para configuração de layout e exibição do toast de termos */
  useEffect(() => {
    setWindowWidth(window.innerWidth);
    setListMenuWidth(listMenuRef.current?.scrollWidth || 0);

    setTimeout(() => {
      const storage = localStorage.getItem(LOCAL_STORAGE_KEY.accept_terms);
      if (!storage || storage === 'false') {
        setShowTermsToast(true);
      }
    }, 1500);
  }, []);

  /** Efeito de debounce para atualização da busca sempre que inputValue ou currentCategory mudam */
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCampaigns({
        page: 1,
        search: inputValue.trim(),
        categoryId: currentCategory,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [inputValue, currentCategory]);

  /** Efeito para atualizar a URL (somente em /explorar)
  useEffect(() => {
    if (window.location.pathname !== '/') {
      const params = new URLSearchParams();
      if (inputValue.trim()) {
        params.set('search', inputValue.trim());
      }
      if (currentCategory) {
        params.set('category', String(currentCategory));
      }
      const newQuery = params.toString();
      // Se a query atual for diferente da desejada, atualiza a URL
      if (newQuery !== window.location.search.substring(1)) {
        router.push(`${window.location.pathname}?${newQuery}`, {
          scroll: false,
        });
      }
    }
  }, [inputValue, currentCategory, router]);

  /** Função de busca manual (pode ser chamada, por exemplo, no clique de um botão) */
  const handleSearch = () => {
    if (inputValue.trim()) {
      const currentPath = window.location.pathname;
      // Se estiver na home, redireciona para '/explorar'
      const baseRoute = currentPath === '/' ? '/explorar' : currentPath;

      router.push(
        `${baseRoute}?search=${encodeURIComponent(inputValue.trim())}`,
        { scroll: false }
      );
      fetchCampaigns({
        page: 1,
        search: inputValue.trim(),
        categoryId: currentCategory,
      });
    }
  };

  /** Seleciona uma categoria e reseta a página para 1 */
  const handleCategorySelect = (categoryId: number | null) => {
    setCurrentCategory(categoryId);
    setCurrentPage(1);
  };

  /** Função para scroll horizontal do menu (usada apenas na home) */
  const scrollListMenu = (value: 'prev' | 'next') => {
    if (value === 'prev' && listMenuPosition === 0) return;
    const newListMenuPosition =
      value === 'prev' ? listMenuPosition - 120 : listMenuPosition + 120;
    listMenuRef.current?.scrollTo({
      left: newListMenuPosition,
      behavior: 'smooth',
    });
  };

  /** Atualiza a posição do scroll do menu */
  const onListMenuScroll = () => {
    setListMenuPosition(listMenuRef.current?.scrollLeft || 0);
  };

  /** Atualiza o valor do input conforme o usuário digita */
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setInputValue(value);
    // Atualiza a URL imediatamente
    const newParams = new URLSearchParams(searchParams);

    if (event.target.value.trim()) {
      newParams.set('search', event.target.value.trim());
    } else {
      newParams.delete('search');
    }

    router.push(`${window.location.pathname}?${newParams.toString()}`, {
      scroll: false,
    });
  };

  /** Se a tecla pressionada for espaço, remove espaços desnecessários */
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Space') {
      setInputValue((prev) => prev.trim());
    }
  };

  const handleSearchButtonClick = () => {
    if (!inputValue.trim()) {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete('search');
      if (!currentCategory) {
        newParams.delete('category');
      }
      const currentPath = window.location.pathname;
      router.push(currentPath + '?' + newParams.toString(), { scroll: false });
      setCurrentPage(1);
      fetchCampaigns({ page: 1, categoryId: currentCategory, search: '' });
      return;
    }

    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set('search', inputValue.trim());
    if (currentCategory) {
      newParams.set('category', String(currentCategory));
    } else {
      newParams.delete('category');
    }
    const currentPath = window.location.pathname;
    router.push(currentPath + '?' + newParams.toString(), { scroll: false });
    setCurrentPage(1);
    fetchCampaigns({
      page: 1,
      categoryId: currentCategory,
      search: inputValue.trim(),
    });
  };

  /** Limpa os filtros de busca e ordenação */
  const clearSearch = () => {
    setInputValue('');
    setCurrentCategory(null);
    setSortBy(null);
    setCurrentPage(1);

    // Limpa a URL e busca imediatamente
    const currentPath = window.location.pathname;
    router.push(currentPath, { scroll: false });

    // Busca campanhas imediatamente ao limpar
    fetchCampaigns({
      page: 1,
      search: '',
      categoryId: null,
    });
  };

  const handleTermsConfirm = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY.accept_terms, JSON.stringify(true));
    setShowTermsToast(false);
  };

  /** Carrega mais campanhas (para paginação infinita, por exemplo) */
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchCampaigns({
        page: currentPage + 1,
        search: inputValue.trim(),
        categoryId: currentCategory,
      });
    }
  };

  return {
    // Estados principais
    inputValue,
    currentCategory,
    loading,
    hasMore,
    campaigns,
    categories,
    showTermsToast,
    sortBy,
    sortOptions,
    currentPage,
    currentFilter,
    isOpen,

    // Refs
    listMenuRef,
    inputRef,
    listMenuPosition,
    listMenuWidth,
    windowWidth,

    // Setters
    setCurrentFilter,
    setSortBy,
    setIsOpen,

    // Handlers de busca e filtros
    handleSearch,
    handleCategorySelect,
    handleSearchButtonClick,
    clearSearch,
    handleInputChange,
    handleKeyDown,
    handleLoadMore,

    // Handlers de layout e termos
    scrollListMenu,
    onListMenuScroll,
    handleTermsConfirm,
  };
}
